import { FC, useEffect, useRef } from "react";

import Hls, { HlsConfig } from "hls.js";
import TubyPlayer from "./TubyPlayer";

const WIDEVINE_LICENSE_URL = "https://34331b7f.drm-widevine-licensing.axprod.net/AcquireLicense";
const PLAYREADY_LICENSE_URL = "https://34331b7f.drm-playready-licensing.axprod.net/AcquireLicense";
const FAIRPLAY_LICENSE_URL = "https://34331b7f.drm-fairplay-licensing.axprod.net/AcquireLicense";
const FAIRPLAY_CERTIFICATE_URL = "/fairplay.cer";

// Safari系(FairPlay対応ブラウザ)でDRM付きコンテンツを再生する経路。
// hls.js(MSE)のFairPlayライセンス要求はAxinomにHTTP 400で拒否されるため使えず、
// video.srcにHLSを直接渡すネイティブ再生 + ネイティブEMEでライセンス取得する。
// (ネイティブ経路はAxinomライセンスサーバで200が返ることを確認済み)
const FAIRPLAY_KEY_SYSTEM_CONFIG: MediaKeySystemConfiguration[] = [{
  initDataTypes: ["sinf", "skd"],
  videoCapabilities: [{ contentType: "video/mp4" }],
  distinctiveIdentifier: "not-allowed",
  persistentState: "not-allowed",
  sessionTypes: ["temporary"],
}];

/**
 * FairPlay が使えるか＝ネイティブ HLS 経路になるか。
 *
 * この経路では hls.js のカスタムローダーが効かず、`video.src` に CDN の m3u8 を
 * 直接渡す。生成中は未生成セグメントが CDN に無いため、再生を始められない。
 * 呼び出し側（SwapPlayer）はこれを見て、生成が終わるまで待つ。
 */
export const canUseFairPlay = async (): Promise<boolean> => {
  if (!navigator.requestMediaKeySystemAccess) {
    return false;
  }
  try {
    await navigator.requestMediaKeySystemAccess("com.apple.fps", FAIRPLAY_KEY_SYSTEM_CONFIG);
    return true;
  } catch (error) {
    return false;
  }
};

const setupNativeFairPlay = (video: HTMLVideoElement, license: string): () => void => {
  let destroyed = false;
  let mediaKeysPromise: Promise<MediaKeys> | null = null;

  const getMediaKeys = () => {
    if (!mediaKeysPromise) {
      mediaKeysPromise = (async () => {
        const access = await navigator.requestMediaKeySystemAccess("com.apple.fps", FAIRPLAY_KEY_SYSTEM_CONFIG);
        const mediaKeys = await access.createMediaKeys();
        const certResponse = await fetch(FAIRPLAY_CERTIFICATE_URL);
        await mediaKeys.setServerCertificate(await certResponse.arrayBuffer());
        await video.setMediaKeys(mediaKeys);
        return mediaKeys;
      })();
    }
    return mediaKeysPromise;
  };

  const onEncrypted = async (event: MediaEncryptedEvent) => {
    try {
      if (!event.initData) {
        return;
      }
      const mediaKeys = await getMediaKeys();
      if (destroyed) {
        return;
      }
      const session = mediaKeys.createSession();
      session.addEventListener("message", async (messageEvent: MediaKeyMessageEvent) => {
        try {
          const response = await fetch(FAIRPLAY_LICENSE_URL, {
            method: "POST",
            headers: { "X-AxDRM-Message": license },
            body: messageEvent.message,
          });
          if (!response.ok) {
            throw new Error(`License request failed: ${response.status}`);
          }
          const ckc = await response.arrayBuffer();
          if (!destroyed) {
            await session.update(ckc);
          }
        } catch (error) {
          console.error("FairPlay license acquisition failed", error);
        }
      });
      await session.generateRequest(event.initDataType, event.initData);
    } catch (error) {
      console.error("FairPlay setup failed", error);
    }
  };

  video.addEventListener("encrypted", onEncrypted);
  return () => {
    destroyed = true;
    video.removeEventListener("encrypted", onEncrypted);
  };
};

export const VideoPlayer: FC<{ src?: string, license?: string, hlsConfig?: Partial<HlsConfig>, onSeek?(currentTime: number, player: HTMLVideoElement): void }> = (props) => {
  const playerRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!playerRef?.current) {
      return
    }

    playerRef.current.addEventListener("seeking", (event) => {
      if (props.onSeek && playerRef?.current) {
        props.onSeek(playerRef.current.currentTime, playerRef.current);
      }
    });
  }, [playerRef?.current]);

  useEffect(() => {
    const video = playerRef?.current;
    if (!video) {
      return
    }
    const src = props.src;
    if (!src) {
      return;
    }

    let disposed = false;
    let cleanup: (() => void) | null = null;

    const startHlsJs = () => {
      const hlsConfig: Partial<HlsConfig> = {
        debug: Boolean((window as any).DEBUG),
        emeEnabled: true,
        drmSystems: {
          'com.widevine.alpha': {
            licenseUrl: WIDEVINE_LICENSE_URL
          },
          'com.microsoft.playready': {
            licenseUrl: PLAYREADY_LICENSE_URL
          }
        },
        licenseXhrSetup: function (xhr, url, keyContext, licenseChallenge) {
          if (props.license) {
            xhr.setRequestHeader('X-AxDRM-Message', props.license);
          }
        },
      };

      if (props.hlsConfig) {
        Object.assign(hlsConfig, props.hlsConfig);
      }

      const hls = new Hls(hlsConfig);
      hls.attachMedia(video);
      hls.loadSource(src);

      cleanup = () => {
        hls.destroy();
      };
    };

    const startNative = () => {
      const cleanupFairPlay = props.license ? setupNativeFairPlay(video, props.license) : null;
      video.src = src;
      cleanup = () => {
        cleanupFairPlay?.();
        video.removeAttribute("src");
        video.load();
      };
    };

    (async () => {
      // 注意: Chromeでもvideo.canPlayType("application/vnd.apple.mpegurl")は"maybe"を返すが
      // 実際にはネイティブ再生できないため、DRM再生の分岐はFairPlayの可用性で判定する。
      const useNativeFairPlay = Boolean(props.license) && (await canUseFairPlay());
      if (disposed) {
        return;
      }
      if (useNativeFairPlay) {
        // Safari系のDRM付き再生: ネイティブHLS + ネイティブEME(FairPlay)
        startNative();
      } else if (Hls.isSupported()) {
        // Chrome/Firefox/Edge等: hls.js + Widevine/PlayReady
        startHlsJs();
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // MSE/ManagedMediaSourceが使えない環境(iOS 17.1未満のSafari等)のフォールバック
        startNative();
      } else {
        console.error("This browser supports neither MSE nor native HLS playback");
      }
    })();

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [props.src, playerRef?.current]);

  return <TubyPlayer disable={props.src == null} playerRef={playerRef} />
};

export default VideoPlayer;
